import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  Stack, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';
import { API } from 'utilities/api';
import { fmtDateTime } from 'utilities/format';
import { EmptyState, StatusChip, GlassIconButton } from 'dashboard/components/common';
import OrderSummaryTiles    from './detail/OrderSummaryTiles';
import OrderLineItemsTable  from './detail/OrderLineItemsTable';
import OrderShipAndHistory  from './detail/OrderShipAndHistory';
import MarkPaidAction       from './detail/MarkPaidAction';
import FulfillAction        from './detail/FulfillAction';
import CancelAction         from './detail/CancelAction';
import RefundAction         from './detail/RefundAction';

const STATUS_COLORS = {
  pending: 'warning',
  paid: 'success',
  fulfilled: 'info',
  cancelled: 'default',
  refunded: 'error',
};

/**
 * Owner-side order detail dialog.
 *
 * Refreshes the full order on open (the list view only carries summary
 * fields). The bottom of the dialog mounts whichever action blocks the
 * current status allows: Mark paid → Fulfill → Cancel / Refund. Each
 * action block lives in its own file under `./detail/` so adding a new
 * transition is a one-file change.
 */
export default function OrderDetailDialog({ open, onClose, brandId, order, onChanged }) {
  const [full, setFull] = useState(order);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null);

  const refresh = useCallback(() => {
    if (!open || !order?._id) return;
    setLoading(true);
    API.getBrandOrder({ brand_id: brandId, _id: order._id })
      .then(setFull)
      .catch((err) => toast.error(err?.message || 'Failed'))
      .finally(() => setLoading(false));
  }, [brandId, order, open]);

  useEffect(() => { refresh(); }, [refresh]);

  // Common wrapper for every action: toast on success / failure,
  // refresh the local + parent list, gate the button via `busy`.
  const guard = async (fn, label) => {
    setBusy(label);
    try {
      await fn();
      toast.success(`${label} ok`);
      await refresh();
      onChanged && onChanged();
    } catch (err) {
      toast.error(err?.message || `${label} failed`);
    } finally {
      setBusy(null);
    }
  };

  const o = full || order;
  if (!o) return null;

  const canPay     = o.status === 'pending';
  const canFulfill = o.status === 'paid';
  const canCancel  = o.status === 'pending' || o.status === 'paid';
  const canRefund  = o.status === 'paid' || o.status === 'fulfilled';

  return (
    <Dialog open={open} onClose={onClose} maxWidth='lg' fullWidth scroll='paper'>
      <DialogTitle sx={{ pr: '48px' }}>
        <Stack direction='row' justifyContent='space-between' alignItems='center' flexWrap='wrap'>
          <Box>
            <Typography variant='h6' fontWeight='700'>{o.number || 'Order'}</Typography>
            <Typography variant='caption' color='text.secondary'>
              {fmtDateTime(o.placed_at)} · {o.source}
            </Typography>
          </Box>
          <StatusChip status={o.status} colors={STATUS_COLORS} />
        </Stack>
        <GlassIconButton label='Close' onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }} size='small'>
          <CloseIcon fontSize='small' />
        </GlassIconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading && !full && <EmptyState>Loading…</EmptyState>}

        <OrderSummaryTiles   order={o} />
        <OrderLineItemsTable order={o} />
        <OrderShipAndHistory order={o} />

        {canPay && (
          <MarkPaidAction
            busy={busy}
            onSubmit={(pay) => guard(() => API.markBrandOrderPaid({
              brand_id: brandId, _id: o._id,
              payment_method: pay.payment_method,
              payment_reference: pay.payment_reference,
            }), 'Mark paid')}
          />
        )}

        {canFulfill && (
          <FulfillAction
            busy={busy}
            onSubmit={(tracking) => guard(() => API.fulfillBrandOrder({
              brand_id: brandId, _id: o._id,
              tracking_carrier: tracking.carrier,
              tracking_number: tracking.number,
            }), 'Fulfill')}
          />
        )}

        {canCancel && (
          <CancelAction
            busy={busy}
            onSubmit={(reason) => guard(() => API.cancelBrandOrder({
              brand_id: brandId, _id: o._id, reason,
            }), 'Cancel')}
          />
        )}

        {canRefund && (
          <RefundAction
            order={o}
            busy={busy}
            onSubmit={({ amount, restock }) => guard(() => API.refundBrandOrder({
              brand_id: brandId, _id: o._id, amount, restock,
            }), 'Refund')}
          />
        )}
      </DialogContent>
      <DialogActions sx={{ p: '14px 20px', justifyContent: 'space-between' }}>
        <Button
          component='a'
          href={`/site/${brandId}/order/${o._id}/receipt`}
          target='_blank'
          rel='noopener noreferrer'
          variant='outlined'
          size='small'
        >
          Open receipt
        </Button>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
